import { cloneDeep } from 'lodash-es';
import { perf } from './performance';
import { generateKey } from './helper';
export class ChangeDetector {
    key;
    parentCd;
    dcuplInitOptions;
    analyticsController;
    off;
    updateListener = new Set();
    // only the "root" counter will be increased and used in the events
    counter = 0;
    constructor() { }
    _init(options) {
        this.key = options.cdRefKey;
        this.analyticsController = options.analyticsController;
        this.parentCd = options.parentCd;
        this.dcuplInitOptions = options.dcuplInitOptions;
        if (this.parentCd) {
            this.off = this.parentCd.on((msg) => {
                if (msg.action === 'update' && msg.originCdRefKey === this.parentCd?.key) {
                    // prevent connect message relay if the message is coming from the parent
                    msg.connectMessage = false;
                    this.trigger(msg);
                }
            });
        }
    }
    getCounter() {
        if (this.parentCd) {
            return this.parentCd.getCounter();
        }
        return this.counter;
    }
    increaseCounter() {
        if (this.parentCd) {
            this.parentCd.increaseCounter();
        }
        else {
            this.counter++;
        }
    }
    destroy() {
        if (this.off) {
            this.off();
        }
    }
    trigger(msg) {
        if (!msg.originCdRefKey) {
            msg.originCdRefKey = this.key;
        }
        if (!msg.key) {
            this.increaseCounter();
            msg.key = this.getCounter();
        }
        if (!msg.end) {
            msg.end = perf.now();
            msg.duration = msg.start - msg.end;
        }
        this.updateListener.forEach((cb) => {
            cb(msg);
        });
    }
    on(cb) {
        this.updateListener.add(cb);
        return () => {
            this.updateListener.delete(cb);
        };
    }
}
export const clone = (obj, shouldClone) => {
    if (!shouldClone) {
        return obj;
    }
    if (typeof structuredClone !== 'undefined') {
        return structuredClone(obj);
    }
    else {
        return cloneDeep(obj);
    }
};
const finish = (targetClass, cdRef, msg, start, context, result) => {
    try {
        const shouldClone = !!cdRef.dcuplInitOptions.performance?.clone?.enabled && !!msg.cloneResult;
        const valueToReturn = clone(result, shouldClone);
        if (msg.scope === 'list') {
            msg.listKey = targetClass.cdRef?.key || targetClass.key;
            msg.modelKey = targetClass.model?.key || targetClass.modelKey;
        }
        if (msg.type === 'list_created') {
            const listKey = msg.args[0].listKey;
            if (listKey.startsWith('__global_list__')) {
                return valueToReturn;
            }
        }
        if (msg.listKey?.startsWith('__global_list__')) {
            return valueToReturn;
        }
        const end = perf.now();
        const duration = end - start;
        const roundedDuration = Math.round(duration * 1000) / 1000;
        msg.duration = roundedDuration;
        msg.start = start;
        msg.end = end;
        const value = {
            duration: msg.duration,
        };
        if (msg.modelKey) {
            value.model = msg.modelKey;
        }
        if (msg.connectMessageResult) {
            msg.result = valueToReturn;
        }
        if (msg.args) {
            value.args = msg.args;
        }
        cdRef?.trigger(msg);
        cdRef.analyticsController.mark({
            name: `${msg.type}:end`,
            context: context,
        });
        cdRef.analyticsController.measure({
            name: msg.type,
            start: `${msg.type}:start`,
            end: `${msg.type}:end`,
            context: context,
            detail: value,
        });
        return valueToReturn;
    }
    catch (err) {
        return result;
    }
};
export const trigger = (_msg) => {
    const log = (_target, _propertyKey, descriptor) => {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            const start = perf.now();
            const cdRef = this.cdRef;
            const msg = clone(_msg, true);
            if (!msg.key) {
                cdRef.increaseCounter();
                msg.key = cdRef.getCounter();
            }
            const context = generateKey();
            cdRef.analyticsController.mark({
                name: `${msg.type}:start`,
                context,
            });
            const modelKey = this.model?.key;
            if (!msg.originCdRefKey && cdRef.key) {
                msg.originCdRefKey = cdRef.key;
            }
            if (modelKey) {
                msg.modelKey = modelKey;
            }
            try {
                const result = originalMethod.apply(this, args);
                msg.method = _propertyKey;
                msg.args = args;
                if (result && typeof result === 'object' && 'then' in result) {
                    return result.then((result) => {
                        return finish(this, cdRef, msg, start, context, result);
                    });
                }
                else {
                    return finish(this, cdRef, msg, start, context, result);
                }
            }
            catch (err) {
                throw err;
            }
        };
        return descriptor;
    };
    return log;
};
//# sourceMappingURL=changeDetection.js.map